import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  SkeletonCircle,
  SkeletonText,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPopularJobRolesListAsync,
  selectPopularJobRolesList,
} from "./PopularJobRolesListSlice";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Footer } from "../../footer/footer";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../common/URLs";
import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  CurrencyRupeeIcon,
} from "@heroicons/react/24/outline";
import { fetchUserRole } from "../../common/FetchUserRole";
import { updateBookmarkAsync } from "../../candidateDashboard/candidateSlice/SavedJobs/SavedJobsSlice";

function PopularJobRolesList() {
  const dispatch = useDispatch();
  const jobs = useSelector(selectPopularJobRolesList);
  // const totalJobs = useSelector(selectPopularJobRolesTotal);

  const { uuid } = useParams();

  useEffect(() => {
    if (uuid) {
      dispatch(fetchPopularJobRolesListAsync({ uuid }));
    }
    window.scrollTo(0, 0);
  }, [dispatch, uuid]);

  const [bookmarkedJobs, setBookmarkedJobs] = useState({});

  const [appliedJobs, setAppliedJobs] = useState({});

  // Prefill applied jobs state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialAppliedJobs = {};
      jobs.forEach((job) => {
        initialAppliedJobs[job.uuid] = job.candidate_job_apply_count === 1;
      });
      setAppliedJobs(initialAppliedJobs);
    }
    window.scrollTo(0, 0);
  }, [jobs]);

  // Prefill bookmark state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialBookmarks = {};
      jobs.forEach((job) => {
        initialBookmarks[job.uuid] = job.candidate_job_saved_count
          ? true
          : false;
      });
      setBookmarkedJobs(initialBookmarks);
    }
    window.scrollTo(0, 0);
  }, [jobs]); // Run when jobs updates

  const toast = useToast();
  const navigate = useNavigate();

  const handleBookmarkClick = async (jobId) => {
    // First check the role and handle redirection if needed
    try {
      // Convert the callback-based fetchUserRole to a Promise
      const role = await new Promise((resolve, reject) => {
        fetchUserRole((role) => {
          resolve(role);
        });
      });

      // If not a candidate, redirect and exit the function
      if (role !== "candidate") {
        toast({
          title: "Please login first",
          description:
            "You need to login as a candidate to save or apply for a job.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        navigate("/login");
        return; // Important: stop execution here
      }

      // Only proceed with bookmark if user is a candidate
      const isCurrentlyBookmarked = bookmarkedJobs[jobId]; // Check current status
      const newStatus = isCurrentlyBookmarked ? "remove" : "save"; // Toggle between "save" and "remove"

      // Optimistic UI update
      setBookmarkedJobs((prev) => ({
        ...prev,
        [jobId]: !isCurrentlyBookmarked,
      }));

      await dispatch(
        updateBookmarkAsync({ job_uuid: jobId, status: newStatus })
      );

      // Show success toast
      toast({
        title: `Job ${newStatus === "save" ? "saved" : "removed"
          } successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update bookmark:", error);

      // Revert on failure - only if we got past the role check
      if (bookmarkedJobs[jobId] !== undefined) {
        setBookmarkedJobs((prev) => ({
          ...prev,
          [jobId]: !prev[jobId], // Revert to previous state
        }));
      }

      // Show error toast
      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  return (
    <div>
      <div className="bg-gray-50 py-8 sm:py-12 min-h-screen">
        <div className="max-w-xl sm:max-w-3xl md:max-w-4xl lg:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Job Posts By <span className="text-blue-600">Roles</span>
              </h2>
              <p className="mt-1 sm:mt-2 text-base sm:text-lg text-gray-600">
                Know your worth and find the job that qualifies your life
              </p>
            </div>
            <Link to="/jobs" className="mt-4 sm:mt-0">
              <button className="text-blue-600 text-base sm:text-lg font-semibold hover:text-blue-800 transition-colors">
                Browse all jobs →
              </button>
            </Link>
          </div>

          {/* Job Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {Array.isArray(jobs) && jobs.length > 0 ? (
              jobs.map((jobItem) => (
                <div key={jobItem.uuid} className="relative">
                  <Link to={`/jobs/${jobItem.uuid}`} className="block h-full">
                    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-3 sm:p-4 md:p-6 h-full">
                      <div className="flex flex-col sm:flex-row items-start sm:space-x-4">
                        {/* Company Logo */}
                        <div className="flex-shrink-0 mb-3 sm:mb-0 mx-auto sm:mx-0">
                          {(jobItem.company_logo) ? (
                            <img
                              className="h-12 w-12 sm:h-16 sm:w-16 rounded-full object-cover"
                              src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${jobItem.company_logo}`}
                              alt={jobItem.company_name || "Company Logo"}
                            />
                          ) : (
                            <Avatar
                              size={{ base: "md", sm: "lg" }}
                              name={jobItem.company_name}
                            />
                          )}
                        </div>

                        {/* Job Details */}
                        <div className="flex-grow space-y-1 w-full text-center sm:text-left">
                          <div className="flex flex-wrap gap-1 justify-center sm:justify-start w-full items-center">
                            <div className="text-lg sm:text-xl font-semibold text-gray-900 line-clamp-2">
                              {jobItem.job_title?.title ||
                                jobItem.job_name ||
                                "No Title"}
                            </div>
                            {jobItem.job_tag && (
                              <Badge
                                colorScheme="blue"
                                borderRadius="full"
                                px={2}
                                py={0.5}
                              >
                                {jobItem.job_tag}
                              </Badge>
                            )}
                          </div>

                          <p className="text-sm text-blue-600">
                            <span className="flex items-center gap-1 font-semibold justify-center sm:justify-start">
                              <BriefcaseIcon className="w-4 h-4 text-zinc-500" />
                              {jobItem.job_name || "Job Name"}
                            </span>
                          </p>

                          <p className="text-sm text-gray-600">
                            <span className="font-medium flex items-center gap-1 justify-center sm:justify-start">
                              <BuildingOffice2Icon className="w-4 h-4 text-zinc-500" />
                              {jobItem.company_name || "Company Name"}
                            </span>
                          </p>

                          {jobItem.department?.title && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium flex items-center gap-1 justify-center sm:justify-start">
                                <BriefcaseIcon className="w-4 h-4 text-zinc-500" />
                                {jobItem.department.title}
                              </span>
                            </p>
                          )}

                          <p className="text-sm text-gray-600">
                            <span className="font-medium flex items-center gap-1 justify-center sm:justify-start flex-wrap">
                              <CurrencyRupeeIcon className="w-4 h-4 text-zinc-500" />
                              <span className="break-all">
                                {jobItem.salary_min_amount === null ||
                                  jobItem.salary_min_amount === 0
                                  ? "Not Disclosed"
                                  : `₹${jobItem.salary_min_amount || "N/A"} - ₹${jobItem.salary_max_amount || "N/A"
                                  } / ${jobItem.salary_per_type || "Not Specified"
                                  }`}
                              </span>
                              <span className="whitespace-nowrap">, {jobItem.job_type}</span>
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>

                  {/* Bookmark Icon - Positioned absolutely for better mobile layout */}
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleBookmarkClick(jobItem.uuid);
                      }}
                      className="bookmark p-1 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow"
                      aria-label={bookmarkedJobs[jobItem.uuid] ? "Remove bookmark" : "Save job"}
                    >
                      {bookmarkedJobs[jobItem.uuid] ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="black"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                            stroke="black"
                            strokeWidth="2"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                            stroke="black"
                            strokeWidth="2"
                          />
                        </svg>
                      )}
                    </button>

                    {appliedJobs[jobItem.uuid] && (
                      <span className="text-green-600 text-xs sm:text-sm font-semibold bg-green-50 px-2 py-1 rounded-full flex items-center gap-1">
                        ✅ Applied
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <>
                {/* Skeleton Loaders */}
                <Box padding="6" boxShadow="lg" bg="white" borderRadius="lg">
                  <SkeletonCircle size="16" />
                  <SkeletonText
                    mt="4"
                    noOfLines={4}
                    spacing="4"
                    skeletonHeight="2"
                  />
                </Box>
                <Box padding="6" boxShadow="lg" bg="white" borderRadius="lg">
                  <SkeletonCircle size="16" />
                  <SkeletonText
                    mt="4"
                    noOfLines={4}
                    spacing="4"
                    skeletonHeight="2"
                  />
                </Box>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default PopularJobRolesList;